import { NOTIFICATION_TYPE } from '@/modules/notification/types';
import {
  OpinionPoll,
  OpinionPollState,
  type OpinionPollVoted,
  TournamentOpinionPoll,
} from '@/modules/opinions';
import type {
  Tournament,
  TournamentsListParams,
} from '@/modules/tournaments/types';
import { ASSET } from '@/modules/wallet/types';
import { RootStore } from '@/store';
import { axiosInstance, formatTokenAmount } from '@/utils';
import { makeAutoObservable } from 'mobx';

export type BankItem = {
  token: ASSET;
  amount: number;
  amountFormatted: string;
};

export class TournamentsStore {
  public opinions: OpinionPoll[] = [];
  public tournaments: Tournament[] = [];
  public uniqueKeyToRefetchList: number = Date.now();
  readonly rootStore: RootStore;

  constructor({ rootStore }: { rootStore: RootStore }) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  public updateUniqueKeyToRefetchList = () => {
    this.uniqueKeyToRefetchList = Date.now();
  };

  public setOpinions = (opinions: OpinionPoll[]) => {
    this.opinions = opinions;
  };

  public setTournaments = (tournaments: Tournament[]) => {
    this.tournaments = tournaments;
  };

  private getRemainingTime(expiresAt: number) {
    const now = Math.floor(Date.now() / 1000);
    const diff = Math.floor(expiresAt / 1000) - now;
    const isPast = diff < 0;
    const absDiff = Math.abs(diff);

    const days = Math.floor(absDiff / 86400);
    const hours = Math.floor((absDiff % 86400) / 3600);
    const minutes = Math.floor((absDiff % 3600) / 60);

    const timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    const formattedTime = days > 0 ? `${days}d ${timeString}` : timeString;

    return isPast ? `${formattedTime} ago` : formattedTime;
  }

  private formatBank(bank: Tournament['bank']): BankItem[] {
    if (!bank) return [];
    const walletStore = this.rootStore.walletStore;
    return Object.keys(bank)
      .filter((key) => bank[key])
      .map((key) => {
        const token = walletStore.mappedAssets[key.toLowerCase()];
        return {
          token,
          amount: bank[key],
          amountFormatted: formatTokenAmount(bank[key], token?.decimals),
        };
      });
  }

  private async formatTotalBank(
    bank: Tournament['bank'],
  ): Promise<Tournament['bankTotal']> {
    if (!bank) return;
    const walletStore = this.rootStore.walletStore;

    const entries = Object.keys(bank)
      .filter((key) => bank[key] && key !== 'VOTE')
      .map(async (key) => {
        const token = walletStore.mappedAssets[key.toLowerCase()];
        if (!token) return 0;
        const tokenRate = await walletStore.getTokenRate(token);
        return Number((bank[key] / 10 ** token.decimals) * tokenRate);
      });

    const amounts = await Promise.all(entries);
    const inUsd = amounts.reduce((acc, val) => acc + val, 0);

    return {
      inUsd,
      inUsdFormatted: formatTokenAmount(inUsd, 0),
    };
  }

  private async formatTournament(tournament: Tournament) {
    tournament.timeLeft = this.getRemainingTime(tournament.expiresAt);
    tournament.bankFormatted = this.formatBank(tournament.bank);
    tournament.bankTotal = await this.formatTotalBank(tournament.bank);
    tournament.minParticipationFormatted = this.formatBank(
      tournament.minParticipation,
    );
    return tournament;
  }

  public getTournamentsStatistic = async () => {
    try {
      const { data } = await axiosInstance.get('/v1/tournaments/stats');

      const transformed = Object.fromEntries(
        Object.entries(data.content).map(([key, value]) => [
          key,
          { available: value },
        ]),
      );

      const response =
        await this.rootStore.walletStore.mapBalancesOnAssets(transformed);

      return {
        vote: response.find(({ code }) => code === 'VOTE')?.balance._available,
        other: formatTokenAmount(
          response.reduce((acc, item) => {
            if (item.code !== 'VOTE') acc += item.balance.inUsd;
            return acc;
          }, 0),
          0,
        ),
      };
    } catch (error) {
      console.error('Error fetching tournaments statistic:', error);
    }
  };

  public getTournaments = async (params: TournamentsListParams) => {
    try {
      const getQueryParams = (params: TournamentsListParams) => {
        const { activeFilter, ...rest } = params;
        if (!activeFilter) return rest;
        return { ...rest, [activeFilter as string]: true };
      };

      const queryParams = getQueryParams(params);
      const { data } = await axiosInstance.get(`/v1/tournaments`, {
        params: queryParams,
      });
      return {
        tournaments: await Promise.all(
          data.content.tournaments.map((tournament: Tournament) =>
            this.formatTournament(tournament),
          ),
        ),
        pagination: data.content.pagination,
      };
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    }
  };

  public getTournamentById = async (id: string, lang: string) => {
    try {
      const { data } = await axiosInstance.get(`/v1/tournaments/${id}`, {
        params: { lang },
      });
      return await this.formatTournament(data.content);
    } catch (error) {
      console.error('Error fetching tournament:', error);
    }
  };

  public getTournamentLeaderboard = async (id: string) => {
    try {
      const { data } = await axiosInstance.get(
        `/v1/tournaments/${id}/leaderboard`,
      );
      return {
        ...data.content,
        callerInfo: {
          ...data.content.callerInfo,
          rewardToClaimFormatted: this.formatBank(
            data.content.callerInfo.rewardToClaim,
          ),
        },
        participants: data.content.participants.map(
          (participant: { name: string }) => ({
            ...participant,
            username: participant.name,
          }),
        ),
      };
    } catch (error) {
      console.error('Error fetching tournament leaderboard:', error);
      return null;
    }
  };

  public getTournamentOpinions = async (id: string, lang: string) => {
    try {
      const { data } = await axiosInstance.get(`/v1/tournaments/${id}/polls`, {
        params: { lang },
      });
      const opinions =
        data.content?.opinionPolls.map(
          (opinion: TournamentOpinionPoll): OpinionPoll | OpinionPollVoted => {
            const item: OpinionPoll | OpinionPollVoted = {
              _id: opinion._id,
              author: {
                ...opinion.author,
                username: opinion.author.name!,
                id: opinion.author._id!,
              },
              title: opinion.title,
              description: null,
              expiresIn: 0,
              state: !!opinion.userChoice
                ? OpinionPollState.VOTED
                : OpinionPollState.NOT_VOTED,
              private: false,
              topics: [],
              banner: opinion.banner,
              emoji: opinion.emoji,
              partnerUrl: null,
              votedFor: opinion.userChoice,
              voters: 0,
              createdBy: '',
              actualPercentage: opinion.userPredict,
              createdAt: 0,
              discussionUrl: null,
              items: opinion.items.map((item) => ({
                title: item.title,
                itemId: item._id,
              })),
              votersChoices: opinion.votersChoices,
            };
            if (item.votersChoices && item.votedFor) {
              item.predictPercentage =
                (item.votersChoices[item.votedFor] /
                  Object.values(item.votersChoices).reduce(
                    (a, b) => a + b,
                    0,
                  )) *
                100;
            }
            return item;
          },
        ) || [];
      this.setOpinions(opinions);
      return opinions;
    } catch (error) {
      console.error('Error fetching tournament opinions:', error);
    }
  };

  public vote = async (params: {
    opinionPollId: string;
    itemId: string;
    predictPercentage: number;
    tournamentId: string;
  }) => {
    try {
      const { data } = await axiosInstance.post(
        `/v1/tournaments/${params.tournamentId}/polls/${params.opinionPollId}/vote`,
        {
          choice: params.itemId,
          predict: params.predictPercentage,
        },
      );
      return data;
    } catch (error) {
      console.error('Error voting on opinion poll:', error);
      return null;
    }
  };

  public claimReward = async (params: { tournamentId: string }) => {
    try {
      const { data } = await axiosInstance.post(
        `/v1/tournaments/${params.tournamentId}/claim`,
      );
      this.updateUniqueKeyToRefetchList();
      return data;
    } catch (error) {
      console.error('Error voting on opinion poll:', error);
      return null;
    }
  };

  public participate = async (params: {
    tournamentId: string;
    paymentAmount: Record<string, number>;
  }) => {
    try {
      const { data } = await axiosInstance.post(
        `/v1/tournaments/${params.tournamentId}/participate`,
        {
          paymentAmount: params.paymentAmount,
        },
      );
      return data;
    } catch (error) {
      console.error('Error participating in tournament:', error);
      const notificationStore = this.rootStore.notificationStore;
      notificationStore.setNotificationType(NOTIFICATION_TYPE.COMMON);
      notificationStore.setNotificationMessage('Insufficient balance');
      return null;
    }
  };

  public updateOpinion = (updatedOpinion: OpinionPoll) => {
    this.opinions = this.opinions.map((opinion) =>
      opinion._id === updatedOpinion._id ? updatedOpinion : opinion,
    );
  };
}
